import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { endpoint, Language } from '@tekman/onmat/constants';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageService } from '@tekman/shared/service';
import { Group } from '@tekman/onmat/models';
import { EnvironmentService } from '../environment/environment.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private _baseUrl: string;
  private languages: string[] = [];
  private languagesObs: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  constructor(
    private readonly http: HttpClient,
    private readonly env: EnvironmentService,
    private readonly storage: StorageService
  ) {}

  public getLanguages(): Observable<string[]> {
    return this.languagesObs.asObservable();
  }

  public setLanguages(languages: string[]) {
    this.languages = languages;
    this.languagesObs.next(this.languages);
  }

  get<T>(path: string, params: any = null): Observable<T> {
    if (params) {
      let setParams = new HttpParams();
      Object.keys(params).forEach(key => {
        setParams = setParams.append(key, params[key]);
      });
      return this.http.get<T>(this.addHost(path), { params: setParams });
    }
    return this.http.get<T>(this.addHost(path));
  }

  getFile<T>(path: string, params: any = null): Observable<T> {
    if (params) {
      let setParams = new HttpParams();
      Object.keys(params).forEach(key => {
        setParams = setParams.append(key, params[key]);
      });
      return this.http.get<T>(this.addHost(path), { params: setParams, responseType: 'blob' as 'json' });
    }
    return this.http.get<T>(this.addHost(path), { responseType: 'blob' as 'json' });
  }

  getGuessStudentGroup<T>(baseAPI: string, academicYearId: string): Observable<Group[]> {
    return this.http.get<Group[]>(this.replaceUrl(baseAPI.concat(endpoint.group.userGroupsByYear), '{academicYearId}', academicYearId));
  }

  post<T>(path: string, body: any, params = null): Observable<T> {
    if (params) {
      let setParams = new HttpParams();
      Object.keys(params).forEach(key => (setParams = setParams.append(key, params[key])));
      return this.http.post<T>(this.addHost(path), { params: setParams });
    }
    return this.http.post<T>(this.addHost(path), body, this.generateHeaders());
  }

  put<T>(path: string, body: any, params = null): Observable<T> {
    if (params) {
      let setParams = new HttpParams();
      Object.keys(params).forEach(key => (setParams = setParams.append(key, params[key])));
      return this.http.put<T>(this.addHost(path), { params: setParams });
    }
    return this.http.put<T>(this.addHost(path), body, this.generateHeaders());
  }

  delete<T>(path: string): Observable<any> {
    return this.http.delete(this.addHost(path), { headers: { 'Content-Type': 'application/json' } });
  }

  private generateHeaders() {
    return {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
  }

  private addHost(path: string): string {
    if (!this._baseUrl) {
      this._baseUrl = this.getBaseFromLang(Language.ESP);
    }
    return this._baseUrl.concat(path);
  }

  /**
   * Choose the correct language for a given student in a School with multiple licenses.
   * @param languages
   * @param academicYearId
   */
  async setApi(languages: string[], academicYearId: string): Promise<void> {
    let baseUrl = this.getBaseFromLang(languages[0]);

    if (!languages.includes(this.storage.languageLicense)) {
      this.storage.removeItem('languageLicense');
    }
    if (languages?.length === 1 && !this.storage.languageLicense) {
      this._baseUrl = baseUrl;
      this.storage.languageLicense = languages[0];
      return;
    }
    if (this.storage.languageLicense) {
      this._baseUrl = this.getBaseFromLang(this.storage.languageLicense);
      return;
    }
    const httpRequestPromises: Promise<Group[]>[] = [];

    languages.forEach(lang => {
      httpRequestPromises.push(this.getGuessStudentGroup(this.getBaseFromLang(lang), academicYearId).toPromise());
    });
    // Get all possible groups from environments.
    const promiseExecution = async () => {
      const promise: any[] = await Promise.allSettled(httpRequestPromises);
      for (let i = 0; i < promise.length; i++) {
        if (promise[i].value.length > 0) {
          // User is in a group... so this is the correct environment
          baseUrl = this.getBaseFromLang(languages[i]);
          this.storage.languageLicense = languages[i];
          i = promise.length;
        }
      }
    };

    await promiseExecution();
    this._baseUrl = baseUrl;
    return;
  }

  getBaseFromLang(language: string): string {
    switch (language) {
      case Language.ECU:
        return `https://${this.env.settings.api.hostECU}/api/`;
      case Language.CAT:
        return `https://${this.env.settings.api.hostCAT}/api/`;
      case Language.ENG:
        return `https://${this.env.settings.api.hostENG}/api/`;
      case Language.MEX:
        return `https://${this.env.settings.api.hostMEX}/api/`;
      default:
        return `https://${this.env.settings.api.hostESP}/api/`;
    }
  }

  replaceUrl(path: string, token: string, value: string): string {
    return path.replace(token, value);
  }

  replaceUrlArray(path: string, tokens: string[], values: string[]): string {
    if (tokens.length !== values.length) {
      return;
    }
    tokens.forEach((token, index) => {
      path = path.replace(token, values[index]);
    });

    return path;
  }
}
